import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
    FileText, 
    Download, 
    Eye, 
    Lock, 
    Shield,
    FolderOpen,
    AlertCircle
} from 'lucide-react';

const DOCUMENT_TYPES = {
    medical: { label: 'Medical Records', color: 'bg-red-100 text-red-800' },
    iep: { label: 'IEP/Education', color: 'bg-blue-100 text-blue-800' },
    legal: { label: 'Legal Documents', color: 'bg-purple-100 text-purple-800' },
    insurance: { label: 'Insurance', color: 'bg-green-100 text-green-800' },
    therapy: { label: 'Therapy Records', color: 'bg-yellow-100 text-yellow-800' },
    school: { label: 'School Records', color: 'bg-orange-100 text-orange-800' },
    other: { label: 'Other', color: 'bg-gray-100 text-gray-800' }
};

const SharedBinderPage = () => {
    const { shareId } = useParams();
    const [binder, setBinder] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [canDownload, setCanDownload] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [needsPassword, setNeedsPassword] = useState(false);
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Fetch shared binder
    const fetchSharedBinder = async (pwd = null) => {
        try {
            setSubmitting(true);
            const payload = pwd ? { password: pwd } : {};
            const response = await api.post(`/shared/${shareId}/access`, payload);
            
            setBinder(response.data.binder);
            setDocuments(response.data.documents);
            setCanDownload(response.data.can_download);
            setNeedsPassword(false);
            setError(null);
        } catch (err) {
            console.error('Error fetching shared binder:', err);
            
            if (err.response?.status === 401) {
                setNeedsPassword(true);
                if (pwd) {
                    toast.error('Invalid password');
                }
            } else {
                setError(err.response?.data?.detail || 'Failed to access shared binder');
            }
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };

    useEffect(() => {
        fetchSharedBinder();
    }, [shareId]);

    // Handle password submit
    const handlePasswordSubmit = (e) => {
        e.preventDefault();
        fetchSharedBinder(password);
    };

    // Download document
    const handleDownloadDocument = async (doc) => {
        try {
            const response = await api.get(`/shared/${shareId}/document/${doc.document_id}`, {
                params: { password: password || undefined }
            });
            
            const docData = response.data;
            
            // Create download link
            const link = window.document.createElement('a');
            link.href = docData.file_data;
            link.download = docData.file_name;
            link.click();
            
            toast.success('Download started');
        } catch (error) {
            console.error('Error downloading document:', error);
            toast.error(error.response?.data?.detail || 'Failed to download document');
        }
    };

    // Format file size
    const formatFileSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    // Format date
    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    // Password required
    if (needsPassword) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center" data-testid="password-prompt">
                <Card className="max-w-md w-full mx-4">
                    <CardHeader className="text-center">
                        <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                            <Lock className="h-8 w-8 text-primary" />
                        </div>
                        <CardTitle>Password Required</CardTitle>
                        <CardDescription>
                            This shared binder is password protected. Please enter the password to continue.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handlePasswordSubmit}>
                            <div className="grid gap-4">
                                <div className="grid gap-2">
                                    <Label htmlFor="password">Password</Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Enter password"
                                        required
                                        data-testid="shared-password-input"
                                    />
                                </div>
                                <Button type="submit" disabled={submitting} data-testid="submit-password-btn">
                                    {submitting ? 'Verifying...' : 'Access Binder'}
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center" data-testid="shared-error">
                <Card className="max-w-md w-full mx-4">
                    <CardHeader className="text-center">
                        <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
                            <AlertCircle className="h-8 w-8 text-destructive" />
                        </div>
                        <CardTitle>Access Denied</CardTitle>
                        <CardDescription>{error}</CardDescription>
                    </CardHeader>
                </Card>
            </div>
        );
    }

    return (
        <div className="container max-w-4xl mx-auto py-8 px-4" data-testid="shared-binder-view">
            {/* Header */}
            <Card className="mb-6">
                <CardHeader>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                        <Shield className="h-4 w-4" />
                        Shared Binder
                    </div>
                    <CardTitle className="flex items-center gap-2">
                        <FolderOpen className="h-6 w-6 text-primary" />
                        {binder?.name}
                    </CardTitle>
                    {binder?.loved_one_name && (
                        <p className="text-muted-foreground">
                            For: {binder.loved_one_name}
                            {binder.relationship && ` (${binder.relationship})`}
                        </p>
                    )}
                    {binder?.description && (
                        <CardDescription className="mt-2">{binder.description}</CardDescription>
                    )}
                    <div className="flex items-center gap-2 mt-4">
                        <Badge variant="outline">
                            {canDownload ? (
                                <><Download className="h-3 w-3 mr-1" /> Download Enabled</>
                            ) : (
                                <><Eye className="h-3 w-3 mr-1" /> View Only</>
                            )}
                        </Badge>
                    </div>
                </CardHeader>
            </Card>

            {/* Documents */}
            <h2 className="text-xl font-semibold mb-4">Documents ({documents.length})</h2>
            
            {documents.length === 0 ? (
                <Card className="text-center py-12" data-testid="no-shared-documents">
                    <CardContent>
                        <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">No documents available</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid gap-4">
                    {documents.map((doc) => (
                        <Card key={doc.document_id} data-testid={`shared-doc-${doc.document_id}`}>
                            <CardContent className="flex items-center justify-between py-4">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                                        <FileText className="h-6 w-6 text-muted-foreground" />
                                    </div>
                                    <div>
                                        <h4 className="font-medium">{doc.name}</h4>
                                        <div className="flex items-center gap-2 mt-1">
                                            <Badge className={DOCUMENT_TYPES[doc.document_type]?.color || DOCUMENT_TYPES.other.color}>
                                                {DOCUMENT_TYPES[doc.document_type]?.label || 'Other'}
                                            </Badge>
                                            <span className="text-sm text-muted-foreground">
                                                {formatFileSize(doc.file_size)}
                                            </span>
                                            <span className="text-sm text-muted-foreground">
                                                {formatDate(doc.created_at)}
                                            </span>
                                        </div>
                                        {doc.description && (
                                            <p className="text-sm text-muted-foreground mt-1">{doc.description}</p>
                                        )}
                                    </div>
                                </div>
                                {canDownload && (
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => handleDownloadDocument(doc)}
                                        data-testid={`download-shared-${doc.document_id}`}
                                    >
                                        <Download className="h-4 w-4 mr-2" />
                                        Download
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Security notice */}
            <Card className="mt-8 bg-muted/50">
                <CardContent className="py-4">
                    <div className="flex items-start gap-3">
                        <Shield className="h-5 w-5 text-muted-foreground mt-0.5" />
                        <div className="text-sm text-muted-foreground">
                            <p className="font-medium text-foreground">Privacy Notice</p>
                            <p>
                                This binder has been shared with you through HAAPE Hub's secure sharing system. 
                                Please handle these documents responsibly and respect the privacy of the individuals involved.
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default SharedBinderPage;
